import React from "react";
import { Link } from "react-router-dom";
import Badge from "../../../shared/Badge";
import { formatDate } from "../../../utils";
import { formatDispatchSnapshotSummary } from "../utils";

/**
 * Readable dispatch snapshot summary for Manifest V2 show page.
 *
 * @param {object} props
 * @param {object} props.snapshot
 */
const DispatchSnapshotSummary = ({ snapshot }) => {
  const summary = formatDispatchSnapshotSummary(snapshot);
  if (!summary) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Dispatch snapshot</h2>
          <p className="text-sm text-gray-500">
            Captured {formatDate(summary.capturedAt, null, true)} — immutable roadside packet
          </p>
        </div>
        <Badge color="indigo" label={summary.complianceProfile || "general_freight"} />
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-md bg-gray-50 p-3 text-sm">
          <p className="text-gray-500">Manifest #</p>
          <p className="font-medium text-gray-900">{summary.manifestNumber}</p>
        </div>
        <div className="rounded-md bg-gray-50 p-3 text-sm">
          <p className="text-gray-500">Legs captured</p>
          <p className="font-medium text-gray-900">{summary.legCount}</p>
        </div>
        <div className="rounded-md bg-gray-50 p-3 text-sm">
          <p className="text-gray-500">Equipment captured</p>
          <p className="font-medium text-gray-900">{summary.equipmentCount}</p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Load</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">BOL</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Commodity</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Miles</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {summary.legs.map((leg) => {
              const jobPath = leg.jobId ? `/jobs/${leg.jobId}` : null;
              return (
                <tr key={leg.legId}>
                  <td className="px-3 py-2">{leg.sortOrder + 1}</td>
                  <td className="px-3 py-2">
                    {jobPath ? (
                      <Link className="text-blue-600 hover:underline" to={jobPath}>
                        {leg.loadNumber}
                      </Link>
                    ) : (
                      leg.loadNumber
                    )}
                    {leg.isHazmat ? (
                      <Badge className="ml-2" color="amber" label="Hazmat" />
                    ) : null}
                  </td>
                  <td className="px-3 py-2">{leg.bolNumber || "—"}</td>
                  <td className="px-3 py-2">{leg.commodity || "—"}</td>
                  <td className="px-3 py-2">{Number(leg.mileage || 0).toFixed(1)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {summary.equipment.length > 0 ? (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-900">Equipment</h3>
          <ul className="mt-2 space-y-1 text-sm text-gray-700">
            {summary.equipment.map((item) => (
              <li key={`${item.role}-${item.equipmentId}`}>
                {item.role === "power_unit" ? "Truck" : "Trailer"}: {item.name || item.equipmentId}
                {item.licensePlate ? ` · ${item.licensePlate}` : ""}
                {item.vin ? ` · VIN ${item.vin}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default DispatchSnapshotSummary;
